import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import type { EntityStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { Group, GroupMemberSearchResults, User, YammerState } from '../../state/types';
import { createFindById } from '../findById';
import { getGroupAdminMemberIds } from '../groupMemberPanels/selectors';
import { getFulfilledUsersOriginNetwork } from '../userOriginNetworks/selectors';
import { getFulfilledUsers } from '../users/selectors';
import { isAllCompanyGroup } from '../viewerCommunityRelationships/selectors';

type GetStatusForId = (state: YammerState, groupId: Group['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, groupId) => getStatus(state.groupMemberSearches, groupId);

export const findById = createFindById('groupMemberSearches');

type GetUserIdsToMemberSearchResultsWithAdmin = (
  userIds: User['id'][],
  adminMemberIds: User['id'][],
  fulfilledUsers: Record<string, User>
) => GroupMemberSearchResults[];
const getUserIdsToMemberSearchResultsWithAdmin: GetUserIdsToMemberSearchResultsWithAdmin = (
  userIds,
  adminMemberIds,
  fulfilledUsers
) =>
  userIds.reduce<GroupMemberSearchResults[]>((acc, edge) => {
    const user = fulfilledUsers[edge];

    if (user) {
      acc.push({
        ...user,
        isAdmin: adminMemberIds.some((adminId) => adminId === user.id),
      });
    }

    return acc;
  }, []);

type GetGroupMemberSearchResults = (state: YammerState, groupId: Group['id']) => GroupMemberSearchResults[];
/**
 * @deprecated PERFORMANCE This selector will return a new array instance every time a selected User entity changes
 * from random API calls etc. Consuming components should only select a memoized list of user ids, then pass each id
 * to a child component which would select the specific User fields it requires and manage its own memoization to
 * avoid unnecessary re-renders.
 */
export const getGroupMemberSearchResults: GetGroupMemberSearchResults = createCachedSelector(
  findById,
  getGroupAdminMemberIds,
  getFulfilledUsers,
  isAllCompanyGroup,
  (groupMemberSearches, adminMemberIds, fulfilledUsers, isAllCompanyGroup) => {
    if (!groupMemberSearches) {
      return [];
    }

    // In some All Company group conditions (static legacy AND migrated) the search will not
    // return all results in groupUserIds and will return most or all results in networkUserIds.
    // For All Company cases the users in both result sets need to be merged due to BE member table design.
    const currentMemberIds = isAllCompanyGroup
      ? [...groupMemberSearches.groupUserIds, ...groupMemberSearches.networkUserIds]
      : groupMemberSearches.groupUserIds;

    return getUserIdsToMemberSearchResultsWithAdmin(currentMemberIds, adminMemberIds, fulfilledUsers);
  }
)((_state, groupId) => groupId);

type GetNetworkUserSearchResults = (state: YammerState, groupId: Group['id']) => GroupMemberSearchResults[];
/**
 * @deprecated PERFORMANCE This selector will return a new array instance every time a selected User entity changes
 * from random API calls etc. Consuming components should only select a memoized list of user ids, then pass each id
 * to a child component which would select the specific User fields it requires and manage its own memoization to
 * avoid unnecessary re-renders.
 */
export const getNetworkUserSearchResults: GetNetworkUserSearchResults = createCachedSelector(
  findById,
  getFulfilledUsers,
  (groupMemberSearches, fulfilledUsers) => {
    if (!groupMemberSearches) {
      return [];
    }

    return getUserIdsToMemberSearchResultsWithAdmin(groupMemberSearches.networkUserIds, [], fulfilledUsers);
  }
)((_state, groupId) => groupId);

export interface GroupMemberSearchResultsWithOriginNetwork extends GroupMemberSearchResults {
  readonly originNetworkId?: string;
}

const emptyGroupMembers: GroupMemberSearchResultsWithOriginNetwork[] = [];

type GetNetworkUserSearchResultsWithOriginNetwork = (
  state: YammerState,
  groupId: Group['id']
) => GroupMemberSearchResultsWithOriginNetwork[];
/**
 * @deprecated PERFORMANCE This selector will return a new array instance every time a selected User entity changes
 * from random API calls etc. Consuming components should only select a memoized list of user ids, then pass each id
 * to a child component which would select the specific User fields it requires and manage its own memoization to
 * avoid unnecessary re-renders.
 */
export const getNetworkUserSearchResultsWithOriginNetwork: GetNetworkUserSearchResultsWithOriginNetwork =
  createCachedSelector(
    findById,
    getFulfilledUsers,
    getFulfilledUsersOriginNetwork,
    (groupMemberSearches, fulfilledUsers, originNetworks) => {
      if (!groupMemberSearches) {
        return emptyGroupMembers;
      }

      const searchUsers = getUserIdsToMemberSearchResultsWithAdmin(
        groupMemberSearches.networkUserIds,
        [],
        fulfilledUsers
      );
      const users = searchUsers.map((user) => ({
        ...user,
        originNetworkId: user.originNetwork ? originNetworks[user.originNetwork].originNetworkId : undefined,
      }));

      return users;
    }
  )((_state, groupId) => groupId);

type GetGroupMemberSearchText = (state: YammerState, groupId: Group['id']) => string;
export const getGroupMemberSearchText: GetGroupMemberSearchText = createCachedSelector(
  findById,
  (groupMemberSearches) => groupMemberSearches?.searchText ?? ''
)((_state, groupId) => groupId);
